import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import type { SelectProps } from "@mui/material";
import { FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";

import { InputError } from "@/components/inputError";

export interface SelectWithErrorProps<T extends string | number = string> extends Omit<SelectProps<T>, "label" | "sx"> {
  errorMsg?: string;
  optional?: boolean;
  label?: React.ReactNode;
  selectSx?: SelectProps<T>["sx"];
  options?: { value: T; label: string; disabled?: boolean }[];
  placeholder?: string;
}

export const SelectWithError = <T extends string | number>({
  errorMsg,
  optional,
  label,
  selectSx,
  options = [],
  placeholder,
  inputProps,
  ...selectProps
}: SelectWithErrorProps<T>): JSX.Element => {
  const hasValue = selectProps.value !== undefined && selectProps.value !== "";
  const isDisabled = Boolean(selectProps.disabled);
  const theme = useTheme();
  const sanitizedInputProps = inputProps ? { ...inputProps } : undefined;

  if (sanitizedInputProps && "shrink" in sanitizedInputProps) {
    delete sanitizedInputProps.shrink;
  }

  return (
    <>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "10px",
              boxShadow: "none",
              marginTop: 1,
              filter: "drop-shadow(1px 1px 5px #ddd)",
            },
          },
        }}
        IconComponent={(props) => (
          <ExpandMoreOutlinedIcon
            {...props}
            sx={{
              ...props.sx,
              "&, & path": {
                color: `${isDisabled ? theme.palette.action.disabled : theme.palette.primary.main} !important`,
                fill: `${isDisabled ? theme.palette.action.disabled : theme.palette.primary.main} !important`,
              },
            }}
          />
        )}
        native={false}
        variant="outlined"
        error={Boolean(errorMsg)}
        label={label}
        displayEmpty={true}
        sx={{
          "&.Mui-disabled": {
            backgroundColor: "action.disabledBackground",
            color: "action.disabled",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "action.disabled",
            },
          },
          ...selectSx,
        }}
        inputProps={sanitizedInputProps}
        renderValue={(selected) => {
          if (!hasValue && placeholder) {
            return <em style={{ color: "gray" }}>{placeholder}</em>;
          }
          const selectedOption = options.find((option) => option.value === selected);
          return selectedOption ? selectedOption.label : "";
        }}
        {...selectProps}
      >
        {placeholder && (
          <MenuItem value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={String(option.value)} value={option.value} disabled={option.disabled}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {errorMsg && <InputError errorMsg={errorMsg} />}
      {optional && <FormHelperText color="info">Optional</FormHelperText>}
    </>
  );
};
